<template>
  <section>
    <form method="post" @submit.prevent="onSend" novalidate>
      <div class="modal-card" style="width: 1000px">
        <header class="modal-card-head">
          <p class="modal-card-title is-centered">
            {{ formTitle }}
          </p>
        </header>

        <section class="modal-card-body">
          <form-wrapper :validator="$v.form">
            <form-group
              horizontal
              name="group_code"
              attribute="attributes.group_code"
              :label="$t('attributes.group_code')"
            >
              <b-autocomplete
                ref="group_code_input"
                v-model="form.group_code"
                name="group_code"
                field="code"
                :disabled="group.group_code"
                :data="groups"
                :loading="isLoadingGroups"
                :placeholder="$t('message.please_input_group_code')"
                :open-on-focus="false"
                @typing="loadGroups"
                @select="onSelectGroup"
              >
                <template #default="props">
                  <p>
                    {{ props.option.code }} -
                    <!-- eslint-disable-next-line-->
                    <span v-html="props.option.name"></span>
                  </p>
                </template>
                <template #empty>
                  {{
                    form.group_code
                      ? isLoadingGroups
                        ? $t('attributes.searching')
                        : $t('attributes.not_found')
                      : $t('message.please_input_group_code')
                  }}
                </template>
              </b-autocomplete>
            </form-group>

            <form-group horizontal name="sort" :label="$t('attributes.sort')">
              <b-select v-model="form.sort" name="sort">
                <option
                  v-for="option in sortOptions"
                  :key="option"
                  :value="option"
                >
                  {{ option }}
                </option>
              </b-select>
            </form-group>

            <form-group
              horizontal
              name="status"
              attribute="attributes.status"
              :label="$t('attributes.status')"
            >
              <b-switch v-model="form.status" name="status"></b-switch>
            </form-group>

            <form-group
              horizontal
              name="name"
              attribute="attributes.topic"
              :label="$t('attributes.topic')"
            >
              <b-input
                v-model="form.name"
                name="name"
                :placeholder="
                  $t('message.please_input') + $t('attributes.topic')
                "
                expanded
              >
              </b-input>
            </form-group>

            <form-group
              horizontal
              name="picture"
              attribute="attributes.picture"
              :label="$t('attributes.picture')"
              grouped
            >
              <template #label>
                {{ $t('attributes.picture') }}
                <b-tooltip type="is-dark" label="400*400/限定jpg檔，100KB內">
                  <b-icon size="is-small" icon="help-circle-outline"></b-icon>
                </b-tooltip>
              </template>
              <b-field class="file">
                <a class="button is-primary" @click="onSelectPictureImage">
                  <b-icon icon="upload"></b-icon>
                  <span>{{ $t('message.please_select_file') }}</span>
                </a>
                <p
                  v-if="form.picture"
                  class="tag is-warning"
                  style="margin-left: 4px; height: 40px"
                >
                  <a
                    :href="projectImageUrl(form.picture)"
                    @click.prevent="onClickImage(projectImageUrl(form.picture))"
                  >
                    {{ form.picture }}
                  </a>
                </p>
              </b-field>
            </form-group>

            <form-group
              horizontal
              name="remark"
              attribute="attributes.description"
              :label="$t('attributes.description')"
            >
              <b-input
                v-model="form.remark"
                type="textarea"
                name="remark"
                :placeholder="
                  $t('message.please_input') + $t('attributes.description')
                "
                maxlength="5000"
                expanded
              >
              </b-input>
            </form-group>
          </form-wrapper>
        </section>

        <footer class="modal-card-foot">
          <b-button class="is-fullwidth" @click="$parent.close()">
            {{ $t('attributes.close') }}
          </b-button>
          <b-button
            :loading="sending"
            type="is-primary"
            class="is-fullwidth"
            native-type="submit"
          >
            {{ this.group.id ? $t('attributes.update') : $t('attributes.add') }}
          </b-button>
        </footer>
      </div>
    </form>

    <b-modal :active.sync="isImageModalActive" :width="400">
      <p class="image">
        <img :src="selectedImageUrl" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import { required, maxLength } from 'vuelidate/lib/validators'
import { OpenProjectFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'
import debounce from 'lodash/debounce'
import { TRAVEL_GROUPS_API } from '@/utils/apis'

export default {
  name: 'TopTravelGroupForm',

  props: {
    group: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      sending: false,

      isLoadingGroups: false,
      groups: [],
      selectedGroupName: null,

      form: this.extractFormValues(this.group),

      sortOptions: [...Array(10).keys()].map(i => i + 1),

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  validations: {
    form: {
      group_code: { required },
      sort: { required },
      status: { required },
      name: { required, maxLength: maxLength(500) },
      remark: { required, maxLength: maxLength(5000) },
      picture: { required, maxLength: maxLength(50) }
    }
  },
  computed: {
    formTitle() {
      let title = this.group.id
        ? this.$t('attributes.modify')
        : this.$t('attributes.add')

      title += this.$t('attributes.top_travel_group')

      return title
    }
  },
  mounted() {
    if (!this.form.group_code) {
      this.$refs.group_code_input.focus()
    }
  },
  watch: {
    group(newGroup) {
      this.form = this.extractFormValues(newGroup)
    }
  },
  methods: {
    extractFormValues(data) {
      return {
        id: data.id,
        group_code: data.group_code,
        sort: data.sort,
        status: data.status,
        name: data.name,
        remark: data.remark,
        picture: data.picture
      }
    },
    async loadAsyncGroups() {
      const api = `${TRAVEL_GROUPS_API}/search`
      const params = {
        code: this.form.group_code
      }

      this.isLoadingGroups = true
      try {
        const res = await this.axios.get(api, { params })
        this.groups = res.data.data
      } catch (e) {
        this.groups = []

        notifyService.fetch_error('collaborate_group', e)
      } finally {
        this.isLoadingGroups = false
      }
    },
    loadGroups: debounce(function() {
      if (!this.form.group_code) {
        this.groups = []
        return
      }

      this.loadAsyncGroups()
    }, 1000),
    onSelectGroup(option) {
      if (!option) {
        this.form.name = ''
        return
      }

      this.form.name = this.StripHTML(option.name)
    },
    async onSelectPictureImage() {
      try {
        this.form.picture = await OpenProjectFinder()
      } catch (e) {
        notifyService.error(e)
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    async onSend() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('send', this.form)
    }
  }
}
</script>
